import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Spinner, Alert, Badge, Card, Button } from 'react-bootstrap';
import { eventService } from '../services/api';
import './FeaturedEvents.css';

// Add this at the top of the file, outside the component
const formatPrice = (price) => {
  if (!price) return 'Gratuit';
  // Remove decimal places and add thousands separator
  return parseInt(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") + ' DZD';
};

function FeaturedEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFeaturedEvents = async () => {
      try {
        setLoading(true);
        // Get upcoming events instead of featured events
        const response = await eventService.getUpcomingEvents();
        
        // Get all upcoming events
        const allEvents = response.data;
        
        // Shuffle the array to get random events
        const shuffled = [...allEvents].sort(() => 0.5 - Math.random());
        
        // Get only three events (or less if there aren't enough)
        const randomThree = shuffled.slice(0, 3);
        
        setEvents(randomThree);
      } catch (error) {
        console.error('Error fetching upcoming events:', error);
        setError('Failed to load upcoming events. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedEvents();
  }, []);

  if (loading) {
    return (
      <Container className="py-5">
        <h2 className="section-title text-center">Événements à venir</h2>
        <div className="text-center my-5">
          <Spinner animation="border" variant="danger" />
          <p className="mt-3">Chargement des événements...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <h2 className="section-title text-center">Événements à venir</h2>
        <div className="text-center my-5 text-danger">
          <i className="fas fa-exclamation-circle fa-3x mb-3"></i>
          <p>{error}</p>
        </div>
      </Container>
    );
  }

  if (events.length === 0) {
    return (
      <Container className="py-5">
        <h2 className="section-title text-center">Événements à venir</h2>
        <div className="text-center my-5">
          <i className="fas fa-calendar-alt fa-3x mb-3 text-muted"></i>
          <p>Aucun événement à venir pour le moment.</p>
          <Link to="/Evenements" className="btn btn-danger mt-3">Voir tous les événements</Link>
        </div>
      </Container>
    );
  }

  return (
    <section id="upcoming-events" className="event-list-page py-5">
      <Container>
        <h2 className="mb-4 text-center">Événements à venir</h2>
        <Row>
          {events.map(event => (
            <Col md={4} key={event.id}>
              <Card className="event-card h-100 shadow-sm">
                {event.image && (
                  <div className="event-image-container">
                    <Card.Img 
                      variant="top" 
                      src={event.image.startsWith('/media') 
                        ? `${event.image.replace('/media', '')}`
                        : `${event.image}`
                      } 
                      alt={event.title} 
                    />
                    {event.is_featured && (
                      <Badge bg="danger" className="featured-badge">
                        <i className="fas fa-star me-1"></i> À la une
                      </Badge>
                    )}
                  </div>
                )}
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <Card.Title>{event.title}</Card.Title>
                    {event.category && (
                      <Badge bg="primary" className="category-badge">
                        {event.category.icon && <i className={`${event.category.icon} me-1`}></i>}
                        {event.category.name}
                      </Badge>
                    )}
                  </div>
                  <div className="event-meta text-muted mb-3">
                    <div><i className="far fa-calendar-alt me-2"></i>{event.formatted_date}</div>
                    <div><i className="far fa-clock me-2"></i>{event.time}</div>
                    <div><i className="fas fa-map-marker-alt me-2"></i>{event.location}</div>
                    {event.wilaya_name && (
                      <div><i className="fas fa-map me-2"></i>{event.wilaya_name}</div>
                    )}
                    <div><i className="fas fa-building me-2"></i>{event.partner_name || "Delta Trae"}</div>
                  </div>
                  <Card.Text>
                    {event.description && (event.description.length > 100 
                      ? `${event.description.substring(0, 100)}...` 
                      : event.description)}
                  </Card.Text>
                </Card.Body>
                <Card.Footer className="bg-white">
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center">
                    <div className="event-price mb-2 mb-sm-0">
                      {event.price ? (
                        <span className="text-danger fw-bold">{formatPrice(event.price)}</span>
                      ) : (
                        <span className="text-success">Gratuit</span>
                      )}
                    </div>
                    <div className="button-group">
                      <Link to={`/event/${event.id}`} className="btn btn-outline-secondary  me-2">
                        <i className="fas fa-info-circle me-1"></i> Détails
                      </Link>
                      <Link 
                        to={`/event/${event.id}/reservation`} 
                        className="btn btn-danger "
                        disabled={event.is_sold_out || event.registration_closed || event.is_past}
                      >
                        <i className="fas fa-ticket-alt me-1"></i> Réserver
                      </Link>
                    </div>
                  </div>
                </Card.Footer>
                {(event.is_sold_out || event.registration_closed || event.is_past) && (
                  <div className="event-overlay">
                    <span className="event-status">
                      {event.is_sold_out ? 'Complet' : event.is_past ? 'Terminé' : 'Inscriptions fermées'}
                    </span>
                  </div>
                )}
              </Card>
            </Col>
          ))}
        </Row>
        <div className="text-center mt-4">
          <Link to="/Evenements" className="btn btn-danger">
            <i className="fas fa-list me-1"></i> Voir tous les événements
          </Link>
        </div>
      </Container>
    </section>
  );
}

export default FeaturedEvents;